import { SdkContext } from '../internal'

/**
 * Access layer for the call4papers module.
 *
 * One method per endpoint, no screen logic in here. The server returns the
 * native payload (the official SDK already unwraps the envelope), so what comes
 * out of here is exactly what the controller built.
 *
 * Two conventions hold for the whole file:
 *
 * 1. **Lists** arrive as `{ items: [...] }` or as a plain array, depending on
 *    the endpoint. `listOf()` takes both — no consumer needs to know which.
 * 2. **A POST with no body** sends `{}` on purpose. With no `Content-Type`,
 *    `express.json()` does not run and `req.body` arrives `undefined` — and
 *    several controllers read `req.body.x` directly.
 */

/** `{ items }` ou array puro — os dois formatos que a casa produz. */
const listOf = <T = any>(result: any): T[] =>
  Array.isArray(result) ? result : (result?.items ?? [])

export class Call4PapersResource {
  constructor(private ctx: SdkContext) {}

  /* =====================================================================
   * Space
   * ================================================================== */

  /**
   * The space configuration plus the visible calls, in one round trip.
   *
   * In `mode: 'single'` the full call comes in `call` — the detail page needs
   * no second request.
   */
  async getSpace(spaceId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/call4papers/spaces/{spaceId}' },
      { spaceId }
    )
  }

  async updateSpace(
    spaceId: string,
    body: {
      mode?: 'single' | 'multi'
      whoCanOpenCall?: string
      requireApprovalToOpen?: boolean
      defaultSettings?: any
    }
  ): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/spaces/{spaceId}', hasBody: true },
      { spaceId, body }
    )
  }

  async listSpaceCalls(spaceId: string, status?: string): Promise<any[]> {
    const result = await this.ctx.raw<any>(
      { httpMethod: 'GET', path: '/call4papers/spaces/{spaceId}/calls' },
      { spaceId, query: status ? { status } : undefined }
    )
    return listOf(result)
  }

  async getBySlug(spaceId: string, slug: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/call4papers/spaces/{spaceId}/calls/{slug}' },
      { spaceId, slug }
    )
  }

  /* =====================================================================
   * Edital
   * ================================================================== */

  async get(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/{id}' }, { id })
  }

  /** `tracks[]` e `eventDates[]` podem vir em bloco — é o que o wizard manda. */
  async create(body: any): Promise<any> {
    return this.ctx.call({ httpMethod: 'POST', path: '/call4papers', hasBody: true }, { body })
  }

  async update(id: string, body: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/{id}', hasBody: true },
      { id, body }
    )
  }

  async remove(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/call4papers/{id}' }, { id })
  }

  /**
   * `{ id, status, publishedAt }` — the same shape for all four. The first
   * publication also answers `emailNotice` (F-104; `notifyByEmail` omitted = on).
   */
  async publish(id: string, notifyByEmail?: boolean): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/publish', hasBody: true },
      { id, body: notifyByEmail === undefined ? {} : { notifyByEmail } }
    )
  }

  async unpublish(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/unpublish', hasBody: true },
      { id, body: {} }
    )
  }

  async close(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/close', hasBody: true },
      { id, body: {} }
    )
  }

  async reopen(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/reopen', hasBody: true },
      { id, body: {} }
    )
  }

  /** A free transition between states. Prefer the four above when they fit. */
  async setStatus(id: string, status: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/{id}/status', hasBody: true },
      { id, body: { status } }
    )
  }

  /* =====================================================================
   * Trilhas e datas
   * ================================================================== */

  async listTracks(id: string): Promise<any[]> {
    return listOf(await this.ctx.raw<any>({ httpMethod: 'GET', path: '/call4papers/{id}/tracks' }, { id }))
  }

  async createTrack(id: string, body: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/tracks', hasBody: true },
      { id, body }
    )
  }

  async updateTrack(trackId: string, body: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/tracks/{trackId}', hasBody: true },
      { trackId, body }
    )
  }

  async deleteTrack(trackId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/call4papers/tracks/{trackId}' },
      { trackId }
    )
  }

  async listEventDates(id: string): Promise<any[]> {
    return listOf(
      await this.ctx.raw<any>({ httpMethod: 'GET', path: '/call4papers/{id}/event-dates' }, { id })
    )
  }

  async createEventDate(id: string, body: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/event-dates', hasBody: true },
      { id, body }
    )
  }

  async updateEventDate(dateId: string, body: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/event-dates/{dateId}', hasBody: true },
      { dateId, body }
    )
  }

  async deleteEventDate(dateId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/call4papers/event-dates/{dateId}' },
      { dateId }
    )
  }

  /* =====================================================================
   * Donos e avaliadores
   * ================================================================== */

  async listOwners(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/{id}/owners' }, { id })
  }

  async addOwner(id: string, memberId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/owners', hasBody: true },
      { id, body: { memberId } }
    )
  }

  async removeOwner(ownerId: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/call4papers/owners/{ownerId}' }, { ownerId })
  }

  async acceptOwnerInvite(ownerId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/owners/{ownerId}/accept', hasBody: true },
      { ownerId, body: {} }
    )
  }

  async declineOwnerInvite(ownerId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/owners/{ownerId}/decline', hasBody: true },
      { ownerId, body: {} }
    )
  }

  /**
   * Invites somebody from the speaker pool to submit to this call.
   *
   * The effect is an email, and only that: it creates no proposal and reserves
   * no slot.
   */
  async inviteSpeaker(callId: string, memberId: string, message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/invite-speaker', hasBody: true },
      { id: callId, body: message ? { memberId, message } : { memberId } }
    )
  }

  /**
   * Who has already proposed in this community, grouped by person.
   *
   * A query over the proposals, not a registry — which is why there is no
   * `create` or `delete` here. Administrators only: the card carries the
   * average score.
   */
  async listCommunitySpeakers(spaceId?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/call4papers/speakers' },
      { query: spaceId ? { spaceId } : undefined }
    )
  }

  async listTrackReviewers(trackId: string): Promise<any[]> {
    return listOf(
      await this.ctx.raw<any>(
        { httpMethod: 'GET', path: '/call4papers/tracks/{trackId}/reviewers' },
        { trackId }
      )
    )
  }

  /** Nasce pendente: quem carimba `acceptedAt` é a pessoa convidada. */
  async addTrackReviewer(trackId: string, memberId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/tracks/{trackId}/reviewers', hasBody: true },
      { trackId, body: { memberId } }
    )
  }

  async removeTrackReviewer(reviewerId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/call4papers/track-reviewers/{reviewerId}' },
      { reviewerId }
    )
  }

  async acceptReviewerInvite(reviewerId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/track-reviewers/{reviewerId}/accept', hasBody: true },
      { reviewerId, body: {} }
    )
  }

  async declineReviewerInvite(reviewerId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/track-reviewers/{reviewerId}/decline', hasBody: true },
      { reviewerId, body: {} }
    )
  }

  /** `{ owners: [...], reviewers: [...] }` — os convites que esperam por mim. */
  async myInvitations(): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/me/invitations' })
  }

  /* =====================================================================
   * Propostas
   * ================================================================== */

  /**
   * Contextual: it returns `viewerRole` — `owner`, `reviewer` or `speaker` —
   * and the shape of the items changes with it. For `speaker` the items are
   * `MySubmission`, not `ReviewQueueItem`, and carry no reviews at all.
   *
   * TODO: no pagination. The endpoint returns the whole call at once, and a
   * conference with 400 proposals sends all 400. Paginating on the client would
   * be a lie: the queue's "to decide", "accepted" and "rejected" counters add up
   * over what arrived, and would add up over the page. It needs `?page`/`?limit`
   * plus a totals block in the response.
   */
  async listSubmissions(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/{id}/submissions' }, { id })
  }

  async createSubmission(id: string, body: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/{id}/submissions', hasBody: true },
      { id, body }
    )
  }

  async mySubmissionsInCall(id: string): Promise<any[]> {
    return listOf(
      await this.ctx.raw<any>({ httpMethod: 'GET', path: '/call4papers/{id}/my-submissions' }, { id })
    )
  }

  /** Todas as minhas propostas na comunidade, de qualquer chamada. */
  async mySubmissions(): Promise<any[]> {
    return listOf(await this.ctx.raw<any>({ httpMethod: 'GET', path: '/call4papers/me/submissions' }))
  }

  async getSubmission(sid: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/submissions/{sid}' }, { sid })
  }

  /** Drafts only, and only the author. */
  async updateSubmission(sid: string, body: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/submissions/{sid}', hasBody: true },
      { sid, body }
    )
  }

  async submitSubmission(sid: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/submit', hasBody: true },
      { sid, body: {} }
    )
  }

  async withdrawSubmission(sid: string, message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/withdraw', hasBody: true },
      { sid, body: message ? { message } : {} }
    )
  }

  /* =====================================================================
   * Avaliação
   * ================================================================== */

  /**
   * Upsert: one review per reviewer per proposal. `submit: false` stores a
   * draft; once submitted it locks. Returns `{ myReview, consensus }` — the
   * consensus comes from the server, never simulated here.
   */
  async saveReview(
    sid: string,
    body: {
      score: number
      recommendation?: string | null
      internalNotes?: string
      commentsToSpeaker?: string
      submit?: boolean
    }
  ): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/submissions/{sid}/review', hasBody: true },
      { sid, body }
    )
  }

  /** `{ consensus, reviews, pendingMyReview? }`. Quem propôs recebe 403. */
  async listReviews(sid: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/submissions/{sid}/reviews' }, { sid })
  }

  /** A minha fila de avaliação em toda a comunidade. */
  async myReviewQueue(): Promise<any[]> {
    return listOf(await this.ctx.raw<any>({ httpMethod: 'GET', path: '/call4papers/me/reviews' }))
  }

  async reviewProgress(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/{id}/review-progress' }, { id })
  }

  /* =====================================================================
   * Voto aberto da comunidade
   * ================================================================== */

  /** A cédula como eu a vejo: `{ callSlug, voteVisibility, canVote, proposals }`. */
  async votableSubmissions(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/{id}/votable-submissions' }, { id })
  }

  async vote(sid: string, value: number): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/call4papers/submissions/{sid}/vote', hasBody: true },
      { sid, body: { value } }
    )
  }

  async removeVote(sid: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/call4papers/submissions/{sid}/vote' },
      { sid }
    )
  }

  async voteResults(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/call4papers/{id}/vote-results' }, { id })
  }

  /* =====================================================================
   * Negociação — append-only dos dois lados
   * ================================================================== */

  async negotiation(sid: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/call4papers/submissions/{sid}/negotiation' },
      { sid }
    )
  }

  /** Contraproposta da organização. Devolve `{ status, round }`. */
  async counterOffer(sid: string, terms: any, message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/counter-offer', hasBody: true },
      { sid, body: { terms, message: message || undefined } }
    )
  }

  /** A organização tira a própria contraproposta da mesa. A rodada fica no
      histórico com tipo `retracted` — a timeline não pode ter buraco. */
  async retractCounterOffer(submissionId: string, message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/counter-offer/retract', hasBody: true },
      { sid: submissionId, body: message ? { message } : {} }
    )
  }

  /** The author's reply — it stays in the same round. */
  async speakerCounter(sid: string, terms: any, message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/speaker-counter', hasBody: true },
      { sid, body: { terms, message: message || undefined } }
    )
  }

  /** Aceite da parte da vez. Devolve `{ status, agreedTerms }`. */
  async acceptOffer(sid: string, message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/accept', hasBody: true },
      { sid, body: message ? { message } : {} }
    )
  }

  /** Recusa do organizador dentro da negociação. */
  async declineOffer(sid: string, message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/decline', hasBody: true },
      { sid, body: message ? { message } : {} }
    )
  }

  /** Decisão direta do dono: `accept` ou `reject`. Devolve `{ status }`. */
  async decide(sid: string, decision: 'accept' | 'reject', message?: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/decide', hasBody: true },
      { sid, body: { decision, message: message || undefined } }
    )
  }

  /** O estado final: agenda a sessão. `scheduledAt` é datetime flutuante. */
  async confirm(sid: string, scheduledAt?: string | null): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/call4papers/submissions/{sid}/confirm', hasBody: true },
      { sid, body: scheduledAt !== undefined ? { scheduledAt } : {} }
    )
  }

  async agreement(sid: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/call4papers/submissions/{sid}/agreement' },
      { sid }
    )
  }
}
